import { isArray } from 'betterplace-utils'
import { Field, connect } from 'formik'
import React, { useCallback, useEffect, useState } from 'react'
import {
  CtaEditorFormValues,
  CtaEditorOuterProps,
  DefaultParsedOption,
  FormCheckboxProps,
  GroupedSelectionOuterProps,
  PreSelectionHandler,
  SmartUrlInputChangeEvent,
  SmartUrlInputProps,
} from './types'
import { FormGroup } from 'src/shared'

export const CtaEditor: React.FC<CtaEditorOuterProps> = connect<CtaEditorOuterProps, CtaEditorFormValues>(
  ({ formik, ...props }) => {
    const [showAdditional, setShowAdditional] = useState<string[]>([])

    useEffect(() => {
      const newShowAdditional = []
      if (formik.values.call_to_action_url) newShowAdditional.push('url')
      if (formik.values.call_to_action_button) newShowAdditional.push('button_text')
      setShowAdditional(newShowAdditional)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      if (formik.values._call_to_action_preselection === 'project-1') {
        const url = new URL(formik.values.call_to_action_url)
        url.searchParams.set('default_interval', `${formik.values._interval}`)
        url.searchParams.set('donation_amount', `${formik.values._amount}`)
        void formik.setFieldValue('call_to_action_url', url.href)
      } else {
        void formik.setFieldValue('call_to_action_url', formik.values.call_to_action_url)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values._call_to_action_preselection, formik.values._interval, formik.values._amount])

    useEffect(() => {
      if (!formik.values._has_call_to_action) {
        void formik.setFieldValue('call_to_action_headline', '')
        void formik.setFieldValue('call_to_action_button', '')
        void formik.setFieldValue('call_to_action_url', '')
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values._has_call_to_action])

    const handlePreSelection: PreSelectionHandler = useCallback(
      (option) => {
        setShowAdditional(option.show_additional || [])
        void formik.setFieldValue('call_to_action_button', option.button_text)
        void formik.setFieldValue('call_to_action_url', option.url)
      },
      [formik]
    )

    return (
      <>
        <Field
          name="_has_call_to_action"
          className="generic-custom-radio-field"
          label={i18n.t(`manage.cta_editor.cta_label_${props.model}`)}
          component={FormCheckbox}
        />

        <div style={{ marginLeft: '25px' }}>
          {formik.values._has_call_to_action && (
            <>
              <Field
                name="call_to_action_headline"
                label={i18n.t('manage.cta_editor.headline_label')}
                component={FormGroup}
                required
              />

              {props.options && (
                <>
                  <Field
                    name="_call_to_action_preselection"
                    label={i18n.t('manage.cta_editor.button_input_label')}
                    component={FormGroup}
                    required
                  >
                    <GroupedSelection
                      id="_call_to_action_preselection"
                      groups={JSON.parse(props.options)}
                      includeEmpty
                      onChange={handlePreSelection}
                    />
                  </Field>
                </>
              )}

              {showAdditional.includes('interval') && (
                <Field
                  name="_interval"
                  label={i18n.t('manage.cta_editor.preset_interval.label')}
                  component={FormGroup}
                  required
                >
                  <select id="_interval" onChange={formik.handleChange}>
                    <option value="single">{i18n.t('manage.cta_editor.preset_interval.values.single')}</option>
                    <option value="monthly">{i18n.t('manage.cta_editor.preset_interval.values.monthly')}</option>
                    <option value="yearly">{i18n.t('manage.cta_editor.preset_interval.values.yearly')}</option>
                  </select>
                </Field>
              )}

              {showAdditional.includes('amount') && (
                <Field
                  name="_amount"
                  label={i18n.t('manage.cta_editor.preset_amount.label')}
                  component={FormGroup}
                  required
                >
                  <div className="input-group">
                    <input
                      type="number"
                      className="form-control"
                      id="_amount"
                      onChange={formik.handleChange}
                      value={formik.values._amount}
                    />
                    <div className="input-group-append">
                      <span className="input-group-text">€</span>
                    </div>
                  </div>
                </Field>
              )}

              {(!props.options || showAdditional.includes('button_text')) && (
                <Field
                  name="call_to_action_button"
                  label={i18n.t('manage.cta_editor.button_label')}
                  component={FormGroup}
                  required
                />
              )}

              {(!props.options || showAdditional.includes('url')) && (
                <Field
                  name="call_to_action_url"
                  label={i18n.t('manage.cta_editor.url_label')}
                  component={FormGroup}
                  required
                >
                  <SmartUrlInput
                    id="call_to_action_url"
                    onChange={formik.handleChange}
                    value={formik.values.call_to_action_url || ''}
                  />
                </Field>
              )}

              <div className="w-full">
                <div className="preview-wrapper">
                  <p className="font-bold">{i18n.t('manage.cta_editor.preview_headline_html')}</p>
                  <div className="generic-preview-container">
                    <div className="generic-call-to-action">
                      <div className="text-3xl">
                        {formik.values.call_to_action_headline || i18n.t('manage.cta_editor.example_headline')}
                      </div>
                      <div className="call-to-action-button">
                        <div className="btn btn-primary btn-large">
                          {formik.values.call_to_action_button || i18n.t('manage.cta_editor.example_button_label')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </>
    )
  }
)

const FormCheckbox = ({ field, form, label }: FormCheckboxProps): JSX.Element => {
  return (
    <div className="form-group mt-6">
      <label className="generic-custom-radio-field" htmlFor={field.name}>
        {}
        <input type="checkbox" {...field} id={field.name} defaultChecked={form.initialValues[field.name]} />
        <span className="generic-custom-radio" />
        <strong>{label}</strong>
      </label>
    </div>
  )
}

const SmartUrlInput = ({
  id,
  onChange: onChange_,
  value,
  prefix = 'https://',
  trim = /^\w*:\/\//,
}: SmartUrlInputProps): JSX.Element => {
  const onChange: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    (e_) => {
      const e = e_ as SmartUrlInputChangeEvent
      e.urlValue = prefix + e.target.value.replace(trim, '')
      return onChange_(e)
    },
    [trim, prefix, onChange_]
  )
  return (
    <div className="input-group">
      <div className="input-group-prepend">
        <span className="input-group-text">{prefix}</span>
      </div>
      <input
        id={id}
        className="form-control"
        name="call_to_action_url"
        onChange={onChange}
        value={value.replace(trim, '')}
      />
    </div>
  )
}

const GroupedSelection = connect<GroupedSelectionOuterProps, CtaEditorFormValues>(
  ({ formik, groups, id, includeEmpty, onChange }) => {
    const customHandleChange: React.ChangeEventHandler<HTMLSelectElement> = useCallback(
      (event) => {
        void formik.setFieldValue('_call_to_action_preselection', event.target.value)
        const [group, index] = event.target.value.split('-')
        const def: DefaultParsedOption = {}
        void formik.setFieldValue(
          'call_to_action_button',
          ((group && index && groups[group]?.[+index]) || def).call_to_action_button
        )
        void formik.setFieldValue(
          'call_to_action_url',
          ((group && index && groups[group]?.[+index]) || def).call_to_action_url
        )
        onChange((group && index && groups[group]?.[+index]) || def)
      },
      [groups, formik, onChange]
    )

    return (
      <select id={id} onChange={customHandleChange} value={formik.values._call_to_action_preselection}>
        {includeEmpty && <option>{i18n.t('manage.cta_editor.preselection_values.please_choose.label')}</option>}
        {Object.keys(groups).map(
          (key) =>
            isArray(groups[key]) &&
            groups[key]?.length && (
              <optgroup key={key} label={i18n.t(`manage.cta_editor.optgroup_label_${key}`)}>
                {groups[key]?.map((opt, index) => (
                  <option key={index} value={`${key}-${index}`}>
                    {opt.label}
                  </option>
                ))}
              </optgroup>
            )
        )}
      </select>
    )
  }
)
